import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, type ChangeEvent, type DragEvent } from "react";
import { analyzeDeck } from "@/lib/deck.functions";
import { posthog } from "@/lib/posthog";
import { getStoredRef } from "@/lib/ref";
import { IkomatchCTA } from "@/components/ikomatch-cta";
import { usePricing } from "@/lib/pricing";


export const Route = createFileRoute("/upload")({
  head: () => ({
    meta: [
      { title: "Upload your deck - DeckScore" },
      { name: "description", content: "Upload your pitch deck to get your investor-readiness score." },
    ],
  }),
  component: Upload,
});

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

function getAnonToken(): string {
  try {
    const k = "deckscore_anon_token";
    let t = localStorage.getItem(k);
    if (!t) { t = crypto.randomUUID(); localStorage.setItem(k, t); }
    return t;
  } catch { return crypto.randomUUID(); }
}

function Upload() {
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const pricing = usePricing();
  const analyze = useServerFn(analyzeDeck);

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.type === "application/pdf") setFile(f);
  }

  function onSelect(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  }

  async function onAnalyze() {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const fileBase64 = await fileToBase64(file);
      const { deckId } = await analyze({
        data: {
          fileBase64,
          fileName: file.name,
          mimeType: file.type || "application/pdf",
          anonToken: getAnonToken(),
          ref: getStoredRef() ?? undefined,
        },
      });

      try { posthog.capture("deck_uploaded", { deck_id: deckId }); } catch { /* non-fatal */ }
      navigate({ to: "/results", search: { deck: deckId } as any });
    } catch (err) {
      console.error(err);
      const raw = err instanceof Error ? err.message : "Something went wrong";
      const msg = /UNREADABLE_PDF/i.test(raw)
        ? "We couldn't read any text from this PDF - it looks image-based or scanned. Please upload a text-based PDF (export your slides so the text is selectable)."
        : /SCORING_FAILED/i.test(raw)
        ? "We couldn't score this deck. Please try again in a minute, or upload a different PDF."
        : /FREE_LIMIT/i.test(raw)
        ? `You've used all 5 free analyses. Reaching investors (${pricing.submissionAmountDisplay}) has no limit.`
        : /RATE_LIMITED/i.test(raw)
        ? "You've hit today's analysis limit. Please try again tomorrow."
        : raw;
      setError(msg);

      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg px-5 py-12 text-ink">
      <div className="mx-auto max-w-[720px]">
        <Link to="/" className="text-sm text-muted-ink hover:text-ink">← Back</Link>
        <div className="mt-6 mb-2 flex items-center gap-2.5">
          <span className="inline-block h-[22px] w-[22px] rounded-md bg-gradient-to-br from-accent to-[#34d399]" />
          <span className="text-lg font-extrabold">DeckScore</span>
        </div>
        <h1 className="mt-4 text-[clamp(28px,4vw,40px)] font-extrabold leading-tight tracking-tight">
          Upload your deck to get your score
        </h1>
        <p className="mt-3 text-[17px] text-muted-ink">
          Drop your PDF below. We'll read every slide against what investors actually check.
        </p>

        <label
          htmlFor="deck-file"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`mt-8 flex min-h-[260px] cursor-pointer flex-col items-center justify-center rounded-2xl border-[1.5px] border-dashed bg-panel-2 p-8 text-center transition ${
            dragOver ? "border-accent bg-accent/5" : "border-[#35485a]"
          } ${busy ? "pointer-events-none opacity-70" : ""}`}
        >
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-2xl text-accent">⬆</div>
          <div className="text-lg font-semibold">
            {file ? file.name : "Drop your deck (PDF)"}
          </div>
          <div className="mt-1 text-sm text-muted-ink">
            {file ? "Ready to analyze" : "or click to choose a file"}
          </div>
          <input id="deck-file" type="file" accept="application/pdf" className="hidden" onChange={onSelect} disabled={busy} />
        </label>

        <button
          type="button"
          disabled={!file || busy}
          onClick={onAnalyze}
          className="mt-6 inline-flex min-h-[52px] w-full items-center justify-center rounded-xl bg-accent px-6 text-[17px] font-bold text-accent-ink transition hover:bg-accent-press disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {busy ? "Reading your deck…" : "Analyze my deck →"}
        </button>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {error && /free.*action|FREE_LIMIT|no limit/i.test(error) && (
          <div className="mt-4">
            <p className="text-sm text-muted-ink mb-2">Out of free analyses?</p>
            <IkomatchCTA
              placement="free_limit"
              variant="unstyled"
              className="inline-flex items-center justify-center min-h-[44px] rounded-full border border-line bg-transparent px-5 py-2 text-sm font-semibold text-ink hover:bg-surface transition"
            >
              Start free investor outreach →
            </IkomatchCTA>
          </div>
        )}
        {!error && (
          <p className="mt-3 text-sm text-muted-ink">
            {busy ? "This usually takes 15-30 seconds." : "Scoring runs against what investors actually check."}
          </p>
        )}
        {/* Persistent ikomatch upsell - always visible, not only during the wait state */}
        <div className="mt-8 rounded-2xl border border-[#35485a] bg-panel-2 p-5">
          <div className="flex items-start gap-4">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent/10 text-lg text-accent">✉</span>
            <div className="min-w-0">
              <h2 className="text-[16px] font-bold leading-snug">
                Rather not chase 30 investors yourself?
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-ink">
                ikomatch writes a personalized cold email to each investor and sends it for you - done-for-you outreach, not a warm-intro nudge.
              </p>
              <div className="mt-4">
                <IkomatchCTA
                  placement={busy ? "wait_state" : "upload_primary"}
                  variant="unstyled"
                  className="inline-flex items-center justify-center min-h-[44px] rounded-full border border-line bg-transparent px-5 py-2 text-sm font-semibold text-ink hover:bg-surface transition"
                >
                  Start free investor outreach →
                </IkomatchCTA>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
